import React, { useEffect, useRef } from 'react';
import { StatusBar } from 'expo-status-bar';
import { NavigationContainer } from '@react-navigation/native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createStackNavigator } from '@react-navigation/stack';
import { lightColors, useTheme } from '@rneui/themed';
import { useTranslation } from 'react-i18next';
import {
  AntDesign,
  Feather,
  Foundation,
  MaterialCommunityIcons,
  MaterialIcons,
} from '@expo/vector-icons';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useAuth } from '../hooks/useAuth';
import { useFirestore } from '../hooks/useFirestore';
import { useConsumption } from '../hooks/useConsumption';
import TodayScreen from './TodayScreen';
import LogScreen from './LogScreen';
import OverviewScreen from './OverviewScreen';
import ChatScreen from './ChatScreen';
import PersonalScreen from './PersonalScreen';
import SignInScreen from './SignInScreen';
import SignUpScreen from './SignUpScreen';
import ResetPasswordScreen from './ResetPasswordScreen';
import ConsumptionContext from '../consumption-context';

const Tab = createBottomTabNavigator();
const Stack = createStackNavigator();

const RootNavigator = () => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { user, reloadUser } = useAuth();
  const {
    addChat,
    addOrUpdateDailyComment,
    subscribeChat,
    subscribeDailyComments,
    subscribeUser,
    updateUser,
    deleteUserAndLogout,
    chat,
    dailyComments,
    fireStoreReady,
    userDoc,
  } = useFirestore();
  const {
    addConsumption,
    confirmZero,
    insertEmptyDays,
    consumption,
    todayByUnit,
    total,
    subscribeConsumption,
  } = useConsumption();
  const hasAddedMissingConsumtion = useRef(false);

  useEffect(() => {
    if (consumption?.length && !hasAddedMissingConsumtion.current) {
      hasAddedMissingConsumtion.current = true;

      async function execute() {
        const fromTo = () => {
          const maxDate = _.maxBy(consumption, (o) => o.datetime)?.datetime;
          let fromDate;
          if (!maxDate) {
            fromDate = dayjs();
          } else {
            fromDate = dayjs(maxDate).add(1, 'days');
          }
          return {
            from: fromDate.startOf('day'),
            to: dayjs().startOf('day'),
          };
        };

        // Execute at least once a day
        try {
          await insertEmptyDays(fromTo());
          const now = dayjs();
          const nextMidnight = dayjs().endOf('day').add(1, 'second');
          const msUntilMidnight = nextMidnight.diff(now);
          const timeout = setTimeout(async () => {
            await insertEmptyDays(fromTo);
            setInterval(() => insertEmptyDays(fromTo()), 24 * 60 * 60 * 1000);
          }, msUntilMidnight);

          return () => clearInterval(timeout);
        } catch (e) {
          console.log('RootNavigator#useEffect', e);
        }
      }
      execute();
    }
  }, [consumption]);

  useEffect(() => {
    if (fireStoreReady) {
      const unsubscribeUser = subscribeUser();
      const unsubscribeConsumption = subscribeConsumption();
      const unsubscribeDailyComments = subscribeDailyComments();
      const unsubscribeChat = subscribeChat();
      return () => {
        unsubscribeUser();
        unsubscribeConsumption();
        unsubscribeChat();
        unsubscribeDailyComments();
      };
    }
  }, [fireStoreReady]);

  return user?.emailVerified ? (
    <ConsumptionContext.Provider
      value={{ userDoc, consumption, dailyComments, total }}
    >
      <NavigationContainer>
        <StatusBar style="auto" />
        <Tab.Navigator
          screenOptions={{
            tabBarActiveTintColor: theme.colors.secondary,
            tabBarInactiveTintColor: lightColors.grey3,
            headerStyle: {
              backgroundColor: theme.colors.primary,
            },
            headerTintColor: 'white',
            headerTitleStyle: {
              fontWeight: 'bold',
            },
          }}
        >
          <Tab.Screen
            name="TodayScreen"
            children={(props) => (
              <TodayScreen
                {...props}
                addConsumption={addConsumption}
                addOrUpdateDailyComment={addOrUpdateDailyComment}
                userDoc={userDoc}
                consumptionTodayByUnit={todayByUnit}
                totalConsumption={total}
              />
            )}
            options={{
              tabBarIcon: ({ focused }) => (
                <MaterialCommunityIcons
                  name="glass-wine"
                  size={26}
                  color={focused ? theme.colors.secondary : lightColors.grey3}
                />
              ),
              title: t('today.title'),
              tabBarAccessibilityLabel: t('today.title'),
              tabBarAccessibilityHint: `${t('navigateTo')} ${t('today.title')}`,
              tabBarTestID: 'tabBarToday',
            }}
          />
          <Tab.Screen
            name="LogScreen"
            children={(props) => (
              <LogScreen
                {...props}
                addConsumption={addConsumption}
                confirmZero={confirmZero}
                addOrUpdateDailyComment={addOrUpdateDailyComment}
              />
            )}
            options={{
              tabBarIcon: ({ focused }) => (
                <AntDesign
                  name="calendar"
                  size={26}
                  color={focused ? theme.colors.secondary : lightColors.grey3}
                />
              ),
              title: t('log.title'),
              tabBarAccessibilityLabel: t('log.title'),
              tabBarAccessibilityHint: `${t('navigateTo')} ${t('log.title')}`,
              tabBarTestID: 'tabBarLog',
            }}
          />
          <Tab.Screen
            name="OverviewScreen"
            children={(props) => <OverviewScreen {...props} />}
            options={{
              tabBarIcon: ({ focused }) => (
                <Foundation
                  name="graph-trend"
                  size={26}
                  color={focused ? theme.colors.secondary : lightColors.grey3}
                />
              ),
              title: t('overview.title'),
              tabBarAccessibilityLabel: t('overview.title'),
              tabBarAccessibilityHint: `${t('navigateTo')} ${t(
                'overview.title'
              )}`,
              tabBarTestID: 'tabBarOverview',
            }}
          />
          {userDoc?.chatEnabled && (
            <Tab.Screen
              name="chatScreen"
              children={(props) => (
                <ChatScreen
                  {...props}
                  addChat={addChat}
                  chat={chat}
                  chatDisabledText={userDoc?.chatDisabledText}
                />
              )}
              options={{
                tabBarIcon: ({ focused }) => (
                  <Feather
                    name="message-square"
                    size={26}
                    color={focused ? theme.colors.secondary : lightColors.grey3}
                  />
                ),
                title: t('chat.title'),
                tabBarAccessibilityLabel: t('chat.title'),
                tabBarAccessibilityHint: `${t('navigateTo')} ${t(
                  'chat.title'
                )}`,
                tabBarTestID: 'tabBarChat',
              }}
            />
          )}
          <Tab.Screen
            name="PersonalScreen"
            children={(props) => (
              <PersonalScreen
                {...props}
                subscribeUser={subscribeUser}
                updateUser={updateUser}
                deleteUserAndLogout={deleteUserAndLogout}
              />
            )}
            options={{
              tabBarIcon: ({ focused }) => (
                <MaterialIcons
                  name="person"
                  size={26}
                  color={focused ? theme.colors.secondary : lightColors.grey3}
                />
              ),
              title: t('personal.title'),
              tabBarAccessibilityLabel: t('personal.title'),
              tabBarAccessibilityHint: `${t('navigateTo')} ${t(
                'personal.title'
              )}`,
              tabBarTestID: 'tabBarPersonal',
            }}
          />
        </Tab.Navigator>
      </NavigationContainer>
    </ConsumptionContext.Provider>
  ) : (
    <NavigationContainer>
      <Stack.Navigator
        screenOptions={{
          headerShown: false,
        }}
      >
        <Stack.Screen
          name="SignInScreen"
          children={(props) => (
            <SignInScreen {...props} reloadUser={reloadUser} />
          )}
        />
        <Stack.Screen name="SignUpScreen" component={SignUpScreen} />
        <Stack.Screen
          name="ResetPasswordScreen"
          component={ResetPasswordScreen}
        />
      </Stack.Navigator>
    </NavigationContainer>
  );
};

export default RootNavigator;
